import React, {useState, useCallback, useRef, useMemo} from "react";
import {Upload as AntUpload} from "antd";
import {useTranslation} from "react-i18next";
import cn from "classnames";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import ButtonDelete from "components/buttons/ButtonDelete";
import {IconDocumentList, IconPlus, IconFolderDalux} from "components/icons";
import {beforeUpload} from "helpers/uploader";
import {useFileDrop} from "helpers/uploadHooks";
import EmptyData from "components/EmptyData";
import {CustomTooltip} from "components/CustomTooltip";
import {dateTimeFormat} from "helpers/datetime";
import {InitialsBadge} from "components/InitialsBadge";
import {getFullName, formFileData} from "helpers/helper";
import {extLinkDomains, extLinkIcons} from "constants/buyer.consts";
import DownloadZip from "../components/buttons/DownloadZip";
import {docType, commonFileExtensions} from "constants/file.consts";
import {Preloader} from "../components/Preloader";
import {hasPermission} from "helpers/helper";
import {LockValue} from "components/LockValue";
import {Info} from "components/Info";
import {color} from "constants/color.consts";

const ButtonAddWrapper = ({prefix, suffix, children}) => (
  <div className="df-row-wrap-center" style={{gap: "10px"}}>
    {prefix}
    {children}
    {suffix}
  </div>
);

const LoadDocsColumn = (props) => {
  const {
    title,
    titleIcon,
    titleLeft,
    docPlaceholder,
    addClass,
    button,
    docs,
    docsIcon,
    editable,
    removable,
    multiple = true,
    addFile,
    removeFile,
    highlightIfEmpty,
    canRemoveAllFiles,
    canAddPermissions,
    loading,
    removeLoading,
    wide,
    notWide,
    halfWide,
    fullWidth,
    loadColumnTitle,
    bottomChildren,
    customAdd,
    isOpenModal,
    linksView,
    customLoadDocument,
    customFileTypes = commonFileExtensions,
    customFileSize = 76800,
    needZipButton,
    needAuthorInfo,
    needShowDeleted,
    currentUser,
    disableZipButton,
    allRevisionsView,
    maxFileRows,
    maxDocs = 9999,
    revisionId,
    modal,
    prefixButtonContent,
    suffixButtonContent,
    druggable = true,
    info,
    infoTooltip,
  } = props;

  const {t} = useTranslation();

  const fileQueue = useRef([]);
  const [fileList, setFileList] = useState([]);
  const [daluxLoading, setDaluxLoading] = useState(false);

  const docsList = useMemo(
    () => (needShowDeleted ? docs : docs.filter((doc) => !doc?.deletedAt)),
    [needShowDeleted, docs]
  );

  const isFoldersDivision = useMemo(
    () => linksView && docsList.some((link) => link?.hasOwnProperty("folderName")),
    [docsList, linksView]
  );
  const isRemoveAllFiles = useMemo(() => {
    const view = !!canRemoveAllFiles && !allRevisionsView && (editable || removable);
    return view && docsList?.length > 1;
  }, [canRemoveAllFiles, allRevisionsView, editable, removable, docsList?.length]);

  const canAddingDocs = useMemo(() => docsList.length < maxDocs, [docsList, maxDocs]);

  const getMemberKey = useCallback(
    (deletedAt) =>
      needShowDeleted && deletedAt
        ? "DeletedOrganizationMember"
        : "AddedOrganizationMember",
    [needShowDeleted]
  );

  const hasFileAuthor = useCallback(
    (doc) => !!doc[getMemberKey(doc.deletedAt)],
    [getMemberKey]
  );
  const hasRevisionName = useCallback(
    (link) =>
      (allRevisionsView && link?.hasOwnProperty("revisionName")) ||
      link?.hasOwnProperty("folderName"),
    [allRevisionsView]
  );
  const hasDirectLink = useCallback(
    (link) =>
      linksView &&
      !link.url?.includes("daluxFile") &&
      !link?.hasOwnProperty("revisionName") &&
      !link?.hasOwnProperty("folderName"),
    [linksView]
  );
  const hasNoFolderDaluxFile = useCallback(
    (link) =>
      linksView && link.url?.includes("daluxFile") && link.url.split("/").length < 7,
    [linksView]
  );

  const authorTooltip = useCallback(
    (doc) => {
      const member = doc[getMemberKey(doc.deletedAt)];
      const orgName = member.Organization.name;
      const date = needShowDeleted ? doc.deletedAt ?? doc.createdAt : doc.updatedAt;
      const prefixLanguageKey =
        needShowDeleted && doc.deletedAt ? "DELETED_BY" : "ADDED_BY";
      const fullName = getFullName(member.User.firstName, member.User.lastName);
      const mainTooltip = `${t(prefixLanguageKey)}:\n${fullName}\n${orgName}`;
      return needShowDeleted
        ? mainTooltip
        : `${mainTooltip}\n${dateTimeFormat(date, "-")}`;
    },
    [t, needShowDeleted, getMemberKey]
  );

  const getAuthorName = (doc, nameKey) => doc[getMemberKey(doc.deletedAt)].User[nameKey];
  const isCurrentUserMember = (doc) => {
    return doc[getMemberKey(doc.deletedAt)].User.id === currentUser?.id;
  };

  const handleAddButtonClick = () => {
    const canAdd = canAddPermissions ? hasPermission(canAddPermissions) : true;
    return canAdd ? (!!customAdd ? customAdd() : true) : undefined;
  };

  const buttonAdd = (
    <LockValue canPermissions={canAddPermissions} needLabel needShowModalToSubscribe>
      <ButtonTransparent
        addClass={`load-document-column__btn-add ${
          highlightIfEmpty && docsList?.length === 0 ? " bg-yellow" : ""
        }`}
        icon={<IconPlus />}
        name={button}
        type="button"
        small
        onClick={handleAddButtonClick}
        loading={(loading && loadColumnTitle === title) || daluxLoading}
        disabled={loading && loadColumnTitle !== title}
        style={{
          width: loading && loadColumnTitle === title ? "120px" : "fit-content",
        }}
      />
    </LockValue>
  );

  const isShowZipButton = needZipButton && !editable && docsList?.length > 1;

  const clickLoad = (e, url) => {
    if (linksView && !url?.includes("daluxFile")) return;
    setDaluxLoading(true);
    e.preventDefault();
    !daluxLoading &&
      !!customLoadDocument &&
      customLoadDocument(url).finally(() => setDaluxLoading(false));
  };

  const rowsStyles = maxFileRows
    ? {
        maxHeight: `${maxFileRows * 20 + 10 * (maxFileRows - 1)}px`,
        overflowY: "scroll",
        overflowX: "hidden",
      }
    : {};

  const toFormData = (file, title) => {
    const data = formFileData(file);
    !!docType[title] && data.append("type", docType[title]);
    return data;
  };

  const handleDropFiles = (droppedFiles) => {
    const files = (multiple ? droppedFiles : droppedFiles.slice(0, 1)).filter(
      (file) => beforeUpload(file, customFileTypes, customFileSize) === true
    );
    if (!files.length) return;
    const formDataList = files.map((file) => toFormData(file, title));
    addFile(formDataList, title);
  };

  const {dropProps, dropClassName} = useFileDrop({
    onFiles: handleDropFiles,
    disabled: !(editable && druggable && !customAdd && canAddingDocs),
    fileTypes: customFileTypes,
  });

  const handleAdd = (e, title) => {
    const files = e.fileList;
    setFileList([]);

    fileQueue.current.push(toFormData(e.file.originFileObj, title));
    if (fileQueue.current.length === files.length) {
      addFile(fileQueue.current, title);
      fileQueue.current = [];
      setFileList([]);
    }
  };

  const filterZipDownload = (doc) =>
    (!doc.hasOwnProperty("revisionName") || !doc.hasOwnProperty("folderName")) &&
    !doc.deletedAt;

  const renderFilesList = () => (
    <>
      {isRemoveAllFiles && (
        <ButtonDelete
          addClass="load-document-column__btn-delete mla mb-2"
          loading={removeLoading}
          onClick={(e) => {
            e.stopPropagation();
            removeFile(null, title);
          }}
          activeOnHover
        />
      )}
      {!!docsList?.length && (
        <div className="load-document-column__items" style={rowsStyles}>
          {docsList.map((item, i) => (
            <div
              key={`${item?.File?.id ?? item?.id}_${i}`}
              className={cn("load-document-column__item", {
                "__revision-name": hasRevisionName(item),
                "__direct-link": hasDirectLink(item),
                "__no-folder-dalux-file": isFoldersDivision && hasNoFolderDaluxFile(item),
              })}
            >
              {allRevisionsView && item?.hasOwnProperty("revisionName") ? (
                <span className="__bold __uppercase">{t(item.revisionName)}</span>
              ) : item?.hasOwnProperty("folderName") ? (
                <React.Fragment>
                  <IconFolderDalux />
                  <span>{item?.folderName}</span>
                  {isShowZipButton && item.filesCount > 1 && (
                    <span className="ml-2">
                      <DownloadZip linksView revisionId={revisionId} folderId={item.id} />
                    </span>
                  )}
                </React.Fragment>
              ) : item?.id === "DALUX_FILES_DIVIDER" ? (
                <span className="wdth-50"></span>
              ) : (
                <React.Fragment>
                  {docsIcon ||
                    (linksView ? (
                      extLinkIcons[
                        extLinkDomains.find((link) => item?.url?.includes(link))
                      ]
                    ) : (
                      <IconDocumentList
                        color={item?.deletedAt ? color.red : color.blue}
                      />
                    ))}
                  <a
                    href={item?.url || item?.File?.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`load-document-column__link${
                      item?.deletedAt ? " red __strikethrough" : ""
                    }`}
                    disabled={daluxLoading || removeLoading}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (customLoadDocument) clickLoad(e, item?.url || item?.File?.url);
                    }}
                  >
                    {item?.name || item?.File?.name}
                  </a>
                  {needShowDeleted && (
                    <span
                      className={`mr-2 ws-nowrap ${
                        item.deletedAt ? "red __strikethrough" : "discrete-text"
                      }`}
                    >
                      {dateTimeFormat(item.deletedAt ?? item.createdAt)}
                    </span>
                  )}
                  {!editable && needAuthorInfo && hasFileAuthor(item) && (
                    <CustomTooltip text={authorTooltip(item)} addClass="ml-2">
                      <span>
                        <InitialsBadge
                          firstName={getAuthorName(item, "firstName")}
                          lastName={getAuthorName(item, "lastName")}
                          isCurrentUser={isCurrentUserMember(item)}
                        />
                      </span>
                    </CustomTooltip>
                  )}
                  {(editable || removable) && (
                    <ButtonDelete
                      addClass="load-document-column__btn-delete"
                      loading={removeLoading}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(item?.id, title);
                      }}
                      activeOnHover
                    />
                  )}
                </React.Fragment>
              )}
            </div>
          ))}
        </div>
      )}

      {!editable && !docsList?.length && <EmptyData text={t("NO_FILES_IN_LIST")} />}

      {/* shown while daluxLoading is true */}
      {daluxLoading && (
        <div className="streaming-external-file dalux">
          <Preloader size={200} />
          <p className="heading">{t("DALUX_LOADING_SPINNER_HEADER")}</p>
          <p>{t("DALUX_LOADING_SPINNER_BODY")}</p>
        </div>
      )}
    </>
  );

  return (
    <div
      className={cn(
        "load-document-column__wrapper",
        {__edit: editable},
        {"__not-wide": notWide},
        {"__half-wide": halfWide},
        {__wide: wide},
        {"__full-width": fullWidth},
        {[addClass]: !!addClass}
      )}
      {...dropProps}
    >
      <div className="load-document-column__wrapper-inner">
        <h4 className={cn("load-document-column__title", {__left: titleLeft})}>
          {titleIcon}
          <span className="text-ellipsis">{t(title)}</span>
          {isShowZipButton && (
            <DownloadZip
              key={docsList.reduce(
                (key, item) =>
                  key.concat(linksView ? item.url : item?.File?.id ?? item?.id),
                ""
              )}
              revisionId={revisionId}
              linksView={linksView}
              docs={docsList
                .filter(filterZipDownload)
                .map((file) => (file?.url ? file : file?.File))
                .filter(Boolean)}
              disabled={disableZipButton}
            />
          )}
          {(info || infoTooltip) && (
            <Info text={t(info)} tooltip={infoTooltip} addClass="ml-4" />
          )}
        </h4>
        <div
          className={cn(
            "load-document-column__body",
            {__edit: editable, "__custom-add": customAdd},
            dropClassName
          )}
        >
          {editable && !customAdd && canAddingDocs ? (
            <AntUpload
              className={cn("load-document-column__upload-dropzone")}
              customRequest={() => {}}
              beforeUpload={(file) => beforeUpload(file, customFileTypes, customFileSize)}
              multiple={multiple}
              fileList={fileList}
              onChange={(e) => handleAdd(e, title)}
              showUploadList={false}
              accept={customFileTypes.join(",")}
              disabled={!druggable}
            >
              <div className="load-document-column__upload-content">
                {renderFilesList()}
              </div>
              <div className="load-document-column__footer">
                {editable && canAddingDocs && (
                  <span className="placeholder">{t(docPlaceholder)}</span>
                )}
                <ButtonAddWrapper
                  prefix={prefixButtonContent}
                  suffix={suffixButtonContent}
                >
                  {buttonAdd}
                </ButtonAddWrapper>
              </div>
            </AntUpload>
          ) : (
            <>
              <div className="load-document-column__upload-content">
                {renderFilesList()}
              </div>
              <div className="load-document-column__footer">
                {editable && canAddingDocs && (
                  <span className="placeholder">{t(docPlaceholder)}</span>
                )}
                <ButtonAddWrapper
                  prefix={prefixButtonContent}
                  suffix={suffixButtonContent}
                >
                  {editable && customAdd && canAddingDocs && buttonAdd}
                </ButtonAddWrapper>
              </div>
            </>
          )}
        </div>
        {isOpenModal && modal}
      </div>
      {bottomChildren}
    </div>
  );
};

export default LoadDocsColumn;
